/*
  CS-89 / W10: behaviour for the native quantity-tier cards (snippets/quantity-tiers.liquid).

  This is the CS-83 contract re-homed onto our own controls, which removes every workaround the
  Foxkit shim needed: the radios are same-origin, so a tier click writes the quantity box
  directly and dispatches a REAL input event (Alpine's x-model follows it), guarded so our own
  write does not re-enter the qty listener. The bracket rule is the shim's, verbatim: on typed
  input, check the highest tier whose min_qty <= value, none below the lowest.

  CS-78's opening state is honoured by NOT running the bracket sync at wire time: whatever the
  box opens at (Avada owns the floor), no tier lights until the shopper acts.

  The deal morph reuses the CS-83 CSS hooks exactly (.add-to-cart-btn[data-deal-active] and the
  injected .cs-deal-label), so the look is already shipped and the shim's own morph retires with
  the app in 2.3, not before.

  Unit prices follow the variant (the CS-53 lesson): cascade:product:variantchange carries
  detail.variant, whose price re-paints every card and the savings line.
*/
(() => {
  // The artboard's two number registers: PRICES always carry both paise digits (₹99.00 struck
  // beside ₹89.10, never ₹99 or ₹89.1), while the savings TOTAL renders bare when whole
  // (you save ₹990). Both match the server-side money filter's 2dp for prices.
  const wholeFormat = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
  const paiseFormat = new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  function money(paise) {
    return '₹' + paiseFormat.format(paise / 100);
  }

  function moneyTotal(paise) {
    const format = paise % 100 === 0 ? wholeFormat : paiseFormat;
    return '₹' + format.format(paise / 100);
  }

  function wire(root) {
    if (root.dataset.csTiersWired) return;
    root.dataset.csTiersWired = '1';

    const scope = root.closest('[data-product-section]') || document;
    const qty = scope.querySelector('input[name="quantity"]');
    const btn = scope.querySelector('button.add-to-cart-btn');
    const radios = Array.from(root.querySelectorAll('input[type="radio"]'));
    const unitEls = Array.from(root.querySelectorAll('[data-tier-unit]'));
    const savings = root.querySelector('[data-cs-tiers-savings]');
    const priceBlock = (root.closest('[data-product-section]') || document).querySelector('[data-product-price-block]');
    if (!qty || !radios.length) return;

    let selfWrite = false;

    const basePrice = () => parseInt(root.dataset.basePrice, 10) || 0;

    function unitFor(radio) {
      const pct = parseFloat(radio.dataset.percent) || 0;
      return Math.round((basePrice() * (100 - pct)) / 100);
    }

    function paintUnits() {
      radios.forEach((radio, index) => {
        const el = unitEls[index];
        if (el) el.textContent = money(unitFor(radio)) + ' each'; // round-7 item 32: "each" is the ruled per-piece term
      });
    }

    const trackHost = root.closest('[data-product-section]') || document.documentElement;
    function sizeTrack() {
      // Founder, 20 Aug fourth pass: the squares are only as big as their own content
      // needs. Measure the widest tile line with wrapping off, size the grid track to
      // it, and share the value with the cross-links swatch row via --cs-tier-track on
      // the section (the 96px css floor covers the no-JS render; the widget sweep
      // asserts every tile stays square, so a measurement miss cannot ship silently).
      const grid = root.querySelector('.cs-tiers__grid');
      if (!grid) return;
      grid.classList.add('cs-tiers--measure');
      let max = 0;
      grid.querySelectorAll('.cs-tiers__qty, .cs-tiers__chip, .cs-tiers__unit').forEach((el) => {
        if (el.scrollWidth > max) max = el.scrollWidth;
      });
      grid.classList.remove('cs-tiers--measure');
      // 8px tile padding + 2px border + 4px air beside the longest line
      trackHost.style.setProperty('--cs-tier-track', Math.max(96, max + 14) + 'px');
    }

    function paintSavings() {
      if (!savings) return;
      const checked = radios.find((radio) => radio.checked);
      const value = parseInt(qty.value, 10);
      const active = !!checked && !Number.isNaN(value) && value >= 1;
      if (active) {
        const unit = unitFor(checked);
        savings.querySelector('[data-savings-unit]').textContent = money(unit);
        savings.querySelector('[data-savings-struck]').textContent = money(basePrice());
        savings.querySelector('[data-savings-amount]').textContent = moneyTotal((basePrice() - unit) * value);
        savings.querySelector('[data-savings-total]').textContent = moneyTotal(unit * value);
      }
      savings.hidden = !active;
      // Founder 19 Aug (fourth wave): ONE price story when a tier is live. The price block
      // strikes its listed price and hides the compare-at via [data-tier-active] CSS (the
      // block itself stays Alpine's; no text is touched), and the sticky bar re-paints from
      // the same event.
      if (priceBlock) priceBlock.toggleAttribute('data-tier-active', active);
      document.body.dispatchEvent(new CustomEvent('cs:tiers:state', {
        detail: {
          active,
          unitPaise: active ? unitFor(checked) : null,
          basePaise: basePrice(),
          qty: active ? value : null,
        },
      }));
    }

    function syncDeal() {
      if (!btn) return;
      const active = !btn.disabled && radios.some((radio) => radio.checked);
      if (active && !btn.querySelector('.cs-deal-label')) {
        const label = document.createElement('span');
        label.className = 'cs-deal-label';
        label.textContent = root.dataset.dealLabel || 'Get this deal!';
        btn.appendChild(label);
      }
      btn.toggleAttribute('data-deal-active', active);
    }

    function syncFromQty() {
      if (selfWrite) return;
      const value = parseInt(qty.value, 10);
      let target = null;
      if (!Number.isNaN(value)) {
        radios.forEach((radio) => {
          if (parseInt(radio.value, 10) <= value) target = radio;
        });
      }
      radios.forEach((radio) => {
        radio.checked = radio === target;
      });
      paintSavings();
      syncDeal();
    }

    qty.addEventListener('input', syncFromQty);
    qty.addEventListener('change', syncFromQty);

    // Founder 19 Aug (second wave): the savings line reads better WITH the price than under
    // the tier grid. Founder 20 Aug (wave 7): the deal price sits ON THE SAME LINE as the
    // struck original, not below the tax line, so the node now lands INSIDE the price block
    // directly after the listed-price span (which wears the strike while a tier is live);
    // the tax line stays below. Every reference is held by this closure, so the paints keep
    // working wherever the node lives.
    if (priceBlock && savings) {
      savings.classList.add('cs-tiers__savings--at-price');
      const listed = priceBlock.querySelector('[data-product-price]');
      if (listed) listed.insertAdjacentElement('afterend', savings);
      else priceBlock.insertAdjacentElement('afterend', savings);
    }

    // The Avada compensating layer (the fifth-wave synchronous clamp, the trust window and
    // the lastAccepted guard) lived here and was REMOVED on 20 Aug 2026, the day the app
    // came off. It existed for one reason: Avada re-seeded its minimum into this input with
    // event-less script writes on its own clock, and the box had to be defended from them.
    // With the minimum now server-rendered by snippets/moq-guard.liquid from our own
    // collection metafields, there is no foreign writer left to defend against, and the
    // guard's own cost is gone with it (it could not tell a legitimate script write from
    // the app's, which is why the theme's minus button once needed a pointer trust window).
    // DO NOT REINTRODUCE without a measured foreign writer: prefer fixing the writer.

    root.addEventListener('change', (event) => {
      if (!event.target || event.target.type !== 'radio') return;
      selfWrite = true;
      qty.value = event.target.value;
      qty.dispatchEvent(new Event('input', { bubbles: true }));
      qty.dispatchEvent(new Event('change', { bubbles: true }));
      selfWrite = false;
      paintSavings();
      syncDeal();
    });

    document.body.addEventListener('cascade:product:variantchange', (event) => {
      const variant = event.detail && event.detail.variant;
      if (variant && typeof variant.price === 'number') {
        root.dataset.basePrice = variant.price;
      }
      paintUnits();
      sizeTrack(); // per-variant prices can lengthen the unit line
      paintSavings();
      syncDeal();
    });

    // CS-78: paint prices and the (inactive) morph, but never preselect a tier at load.
    paintUnits();
    sizeTrack();
    syncDeal();
  }

  function init() {
    document.querySelectorAll('[data-cs-tiers]').forEach((root) => {
      try {
        wire(root);
      } catch (error) {
        /* ponytail: a widget that cannot wire still shows server-rendered prices and links; the
           shopper loses only the live sync, never the buy box. */
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
